import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { SessionService } from './session.service';
import { MobileStorageService } from './mobile-storage.service';
import { EndpointsService } from './endpoints.service';
import { ApiResponse } from '../core/models/api-response.model';
import { SidewallOcr, SidewallOCRResponseData } from '../core/models/sidewall-ocr.model';
import { TireFormData } from '../core/models/tire-form-data.model';

@Injectable({ providedIn: 'root' })
export class CaptureService {
  private _currentImageBase64 = signal<string | null>(null);
  private _lastOcrResult = signal<SidewallOcr | null>(null);
  private _savedFormState = signal<TireFormData | null>(null);
  private _isManualEntry = signal<boolean>(false);
  private _currentRoute = signal<string>('/home');

  private http = inject(HttpClient);
  private session = inject(SessionService);
  private mobileStorage = inject(MobileStorageService);
  private endpointsService = inject(EndpointsService);

  public setImage(base64: string, route?: string): void {
    this._currentImageBase64.set(base64);
    this._savedFormState.set(null);
    this._isManualEntry.set(false);
    if (route) {
      this._currentRoute.set(route);
    }
    this.autoSaveState();
  }

  public getImage(): string | null {
    return this._currentImageBase64();
  }

  public setOcrResult(ocr: SidewallOcr | null, route?: string): void {
    this._lastOcrResult.set(ocr);
    if (route) {
      this._currentRoute.set(route);
    }
    this.autoSaveState();
  }

  public getOcrResult(): SidewallOcr | null {
    return this._lastOcrResult();
  }

  public setSavedFormState(data: TireFormData, route?: string): void {
    this._savedFormState.set(data);
    if (route) {
      this._currentRoute.set(route);
    }
    this.autoSaveState();
  }

  public getSavedFormState(): TireFormData | null {
    return this._savedFormState();
  }

  public saveCurrentRoute(route: string): void {
    this._currentRoute.set(route);
    this.autoSaveState();
  }

  public getCurrentRoute(): string {
    return this._currentRoute();
  }

  public startManualEntry(route?: string): void {
    this._currentImageBase64.set(null);
    this._lastOcrResult.set(null);
    this._savedFormState.set(null);
    this._isManualEntry.set(true);
    if (route) {
      this._currentRoute.set(route);
    }
    this.autoSaveState();
  }

  public isManualEntry(): boolean {
    return this._isManualEntry();
  }

  public clearImage(): void {
    const sessionGuid = this.session.currentSession()?.tireSpecSessionGuid;
    this._currentImageBase64.set(null);
    this._lastOcrResult.set(null);
    this._savedFormState.set(null);
    this._isManualEntry.set(false);
    this._currentRoute.set('/home');

    if (sessionGuid) {
      this.mobileStorage.clearState(sessionGuid);
    }
  }

  private autoSaveState(): void {
    const currentSession = this.session.currentSession();
    if (!currentSession?.tireSpecSessionGuid) {
      return;
    }
    this.mobileStorage.saveState(currentSession.tireSpecSessionGuid, {
      deviceInstanceId: this.session.getDeviceInstanceId(),
      currentRoute: this._currentRoute(),
      capturedImage: this._currentImageBase64(),
      ocrResult: this._lastOcrResult(),
      formState: this._savedFormState(),
      isManualEntry: this._isManualEntry()
    });
  }

  public async restoreFromStorage(sessionGuid: string): Promise<{ restored: boolean; route?: string }> {
    if (!sessionGuid) {
      return { restored: false };
    }

    const state = await this.mobileStorage.getState(sessionGuid);
    const deviceId = this.session.getDeviceInstanceId();

    if (!state || state.deviceInstanceId !== deviceId) {
      if (state) {
        await this.mobileStorage.clearState(sessionGuid);
      }
      return { restored: false };
    }
    this._currentImageBase64.set(state.capturedImage);
    this._lastOcrResult.set(state.ocrResult);
    this._savedFormState.set(state.formState);
    this._isManualEntry.set(state.isManualEntry);
    this._currentRoute.set(state.currentRoute || '/home');

    return { restored: true, route: state.currentRoute };
  }

  private async dataURItoBlob(dataURI: string): Promise<Blob> {
    const res = await fetch(dataURI);
    return await res.blob();
  }

  public uploadSidewallImage(): Observable<SidewallOcr> {
    const image = this._currentImageBase64();
    const sessionData = this.session.currentSession();

    if (!image) {
      return throwError(() => new Error('No image provided'));
    }
    
    if (!sessionData) {
      return throwError(() => new Error('No active session found'));
    }

    return from(this.dataURItoBlob(image)).pipe(
      switchMap(blob => {
        const formData = new FormData();
        formData.append('image', blob, 'sidewall.jpg');

        return this.http.post<ApiResponse<SidewallOCRResponseData>>(
          this.endpointsService.processOcr, 
          formData
        );
      }),
      map(response => {
        const ocr = response.data?.SidewallOCR || {} as SidewallOcr;
        this.setOcrResult(ocr, '/sidewall-details');
        return ocr;
      }),
      catchError(err => {
        console.error('OCR Backend Failed:', err);
        this.setOcrResult(null);
        return throwError(() => err);
      })
    );
  }
}
